// Derive the journey set reachable from an entry journey: cumulative days per
// touchpoint, and each sub-journey attached under its LATEST trigger (deduped,
// with earlier triggers recorded as secondaries). Pure.
import { resolveSegmentRefs } from './fixtures/segments';
import { isoToDays } from './isoDuration';

export function deriveModel(entryJourneyId, { journeys, segments }) {
  const segById = new Map(segments.map((s) => [s.segment_id, s]));
  const tpIndex = {}; // touchpoint_id -> { tp, journey }
  journeys.forEach((j) => j.touchpoints.forEach((tp) => { tpIndex[tp.touchpoint_id] = { tp, journey: j }; }));

  // trigger set for a journey = touchpoints whose response attr its segment references
  const triggerSet = (j) => {
    const refIds = new Set(resolveSegmentRefs(segById.get(j.segment_id), segById).map((r) => String(r.attribute_id)));
    if (!refIds.size) return [];
    return journeys.flatMap((src) => src.touchpoints)
      .filter((tp) => tp.response_attribute_id != null && refIds.has(String(tp.response_attribute_id)))
      .map((tp) => tp.touchpoint_id);
  };

  // cumulative day per touchpoint, resolving journeys in trigger-topological order
  const dayOf = {}; const started = {};
  const resolve = (j) => {
    if (started[j.journey_id]) return;
    started[j.journey_id] = true;                       // set BEFORE recursing so a cycle terminates
    const trig = triggerSet(j);
    trig.forEach((tid) => resolve(tpIndex[tid].journey));
    const known = trig.map((tid) => dayOf[tid]).filter((d) => d != null);
    const start = known.length ? Math.max(...known) : 0; // ignore triggers whose day is unresolved (cycle)
    let cum = start;
    [...j.touchpoints].sort((a, b) => a.ordinal - b.ordinal).forEach((tp) => { cum += isoToDays(tp.wait_time); dayOf[tp.touchpoint_id] = cum; });
  };
  journeys.forEach(resolve);

  const latestTrigger = (j) => { const t = triggerSet(j); return t.length ? t.reduce((a, b) => (dayOf[a] >= dayOf[b] ? a : b)) : null; };
  const childrenOf = {}; journeys.forEach((j) => { childrenOf[j.journey_id] = []; });
  journeys.forEach((j) => { const lt = latestTrigger(j); if (lt) childrenOf[tpIndex[lt].journey.journey_id].push(j); });

  const built = (j) => {
    const lt = latestTrigger(j);
    const trig = triggerSet(j);
    return {
      journeyId: j.journey_id, name: j.name, segment: segById.get(j.segment_id) || null,
      isRoot: trig.length === 0, primaryTriggerTpId: lt,
      secondaryTriggerTpIds: trig.filter((tid) => tid !== lt),
      touchpoints: [...j.touchpoints].sort((a, b) => a.ordinal - b.ordinal)
        .map((tp, i) => ({ ...tp, day: dayOf[tp.touchpoint_id], isEntry: i === 0 })),
    };
  };

  // pre-order from the entry journey
  const out = []; const seen = new Set();
  const walk = (jid) => {
    if (seen.has(jid)) return; seen.add(jid);
    const j = journeys.find((x) => x.journey_id === jid); if (!j) return;
    out.push(built(j));
    childrenOf[jid].forEach((c) => walk(c.journey_id));
  };
  walk(entryJourneyId);
  return { rootId: entryJourneyId, journeys: out };
}
